import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAcademicSessions,
  selectAllSessions,
  selectSessionsLoading,
  selectSessionsError,
} from "../../../Slices/sessionsSlice";
import { isAuthenticated, addHoliday } from "../../../ApiService/ApiService";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CustomDialog from "../../../Components/CustomDialog";

function AddHoliday({
  setShowAddHoliday,
  setSelectedSessionId,
  fetchHolidaysBySessionId,
}) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchAcademicSessions());
  }, [dispatch]);

  const sessions = useSelector(selectAllSessions);
  const sessionsLoading = useSelector(selectSessionsLoading);
  const sessionsError = useSelector(selectSessionsError);
  const schoolId = parseInt(sessionStorage.getItem("schoolId"));

  const [formData, setFormData] = useState({
    name: "",
    startDate: "",
    endDate: "",
    sessionId: "",
    schoolId: schoolId,
  });

  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "startDate" || name === "endDate") {
      const formattedDate = new Date(value).toISOString().split("T")[0];
      setFormData({
        ...formData,
        [name]: formattedDate,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isAuthenticated()) {
        const response = await addHoliday(formData);
        if (response && response?.data?.status === 200) {
          setDialog({
            visible: true,
            title: "Success",
            message: "Holiday added",
            buttons: [{ name: "OK", onPress: handleDialogClose }],
          });
          setSelectedSessionId(formData.sessionId);
          fetchHolidaysBySessionId(formData.sessionId);
          setFormData({
            name: "",
            startDate: "",
            endDate: "",
            sessionId: "",
            schoolId: schoolId,
          });
          setShowAddHoliday(false);
        } else {
          const errorMessage =
            response?.data?.errorMessage || "Something went wrong";
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Error",
          message: "Invalid response from the server",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      console.error("Failed to add holiday", error);
      setDialog({
        visible: true,
        title: "Error",
        message: "Failed to add holiday",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box sx={{ maxWidth: "75%", margin: "auto", padding: 3 }}>
        <Paper elevation={3} sx={{ padding: 5 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: 22, color: "black" }}
          >
            Add Holiday
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="sessions">Sessions</InputLabel>
                  <Select
                    name="sessionId"
                    label="Sessions"
                    labelId="sessions"
                    value={formData.sessionId}
                    onChange={handleChange}
                    size="small"
                    disabled={sessionsLoading || sessionsError}
                  >
                    {sessionsLoading ? (
                      <MenuItem value="">Loading...</MenuItem>
                    ) : sessionsError ? (
                      <MenuItem value="">Error fetching classes</MenuItem>
                    ) : (
                      sessions?.data?.map((currSession) => (
                        <MenuItem key={currSession.id} value={currSession.id}>
                          {currSession.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={6}>
                <Typography
                  sx={{ fontSize: 14, marginBottom: 1, marginLeft: 1 }}
                >
                  Start Date
                </Typography>
                <TextField
                  fullWidth
                  name="startDate"
                  type="date"
                  labelid="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={6}>
                <Typography
                  sx={{ fontSize: 14, marginBottom: 1, marginLeft: 1 }}
                >
                  End Date
                </Typography>
                <TextField
                  fullWidth
                  name="endDate"
                  type="date"
                  labelid="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={() => setShowAddHoliday(false)}
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: "unset" }}
                >
                  Back
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ textTransform: "unset" }}
                >
                  {loading ? "Loading..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </>
  );
}

export default AddHoliday;
